import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AddItemDto } from '@azoupvendas/shared';

// AIDEV-NOTE: Estado global do pedido sendo criado (wizard multi-step).
// preco_varejo_atacado vem do cadastro do cliente no Firebird e
// tem precedencia maxima na logica de pricing (override do cliente).

export interface ClienteSelecionado {
  CODIGO: number;
  RAZAO_SOCIAL: string;
  CNPJ_CPF: string | null;
  CIDADE: string;
  preco_varejo_atacado?: 'V' | 'A';
  // AIDEV-NOTE: Limite de desconto especial (politicas_cliente) — null se nao configurado.
  // Substitui os limites do vendedor no Step3 e libera desconto sem permissao.
  limite_desconto_especial?: number | null;
}

// AIDEV-NOTE: Filtros do catalogo (Step2). Ficam no store para sobreviver a navegacao
// entre steps do wizard (o Step2Catalogo desmonta ao ir pro carrinho). Limpos no resetPedido.
export interface FiltrosCatalogo {
  busca: string;
  secao: string;
  grupo: string;
  subgrupo: string;
  filtrosAbertos: boolean;
  // AIDEV-NOTE: Caminho na navegacao por grupos — array de VALORES dos nos ('' e o balde
  // "Sem grupo", por isso nao da para reaproveitar secao/grupo/subgrupo, onde '' = sem filtro).
  // Mora aqui (e nao no componente) para sobreviver a ida ao carrinho e voltar.
  caminhoArvore: string[];
}

interface PedidoStore {
  // Step 1: cliente selecionado
  clienteSelecionado: ClienteSelecionado | null;
  // Itens no carrinho
  itens: AddItemDto[];
  // AIDEV-NOTE: precosOriginais é paralelo a itens[]: guarda o valor_unitario no momento
  // em que o item foi adicionado ao carrinho. Nunca atualizado por updateItem.
  // Usado pelo CarrinhoItem para detectar se o preço foi alterado manualmente.
  precosOriginais: number[];
  // Tabela de preco selecionada (modo 1 - vendedor seleciona tabela)
  tabelaPrecoId: number | null;
  // Filtros do catalogo (persistem entre steps; limpos no resetPedido)
  filtrosCatalogo: FiltrosCatalogo;
  // Step atual do wizard
  stepAtual: number;
  // Pedido ID (apos criar rascunho via POST /api/pedidos)
  pedidoId: string | null;
  // AIDEV-NOTE: Finding 1 (final review) — origem do draft persistido. 'novo' quando o rascunho
  // nasceu no fluxo de criacao (NovoPedidoPage), 'edicao' quando EditarPedidoPage populou o store
  // para editar um pedido existente. Sem isso, sair de /pedidos/:id/editar sem resetar deixava o
  // draft de edicao no localStorage; abrir /pedidos/novo depois oferecia "continuar" esse pedido —
  // se finalizado/erro_sync (numero null), o wizard sem editMode disparava autosave (PATCH /itens)
  // contra um pedido que nao aceita mais edicao -> 400 INVALID_STATUS_FOR_AUTOSAVE em retry infinito
  // e Finalizar travado para sempre.
  draftOrigem: 'novo' | 'edicao' | null;
  // AIDEV-NOTE: Finding 3 (final review) — vendedor dono do draft persistido. Dispositivo
  // compartilhado: sem isso, o vendedor B via o diálogo de recuperação com o carrinho do
  // vendedor A (vazamento de dados + 404/403 de RBAC no autosave). NAO limpar no logout — o
  // draft precisa sobreviver a logout forcado do MESMO usuario (cenario do incidente 2026-08).
  draftVendedorId: number | null;

  // Actions
  setCliente: (c: ClienteSelecionado | null) => void;
  addItem: (item: AddItemDto) => void;
  removeItem: (index: number) => void;
  updateItem: (index: number, updates: Partial<AddItemDto>) => void;
  // AIDEV-NOTE: setItens substitui todos os itens de uma vez (usado no modo edicao de rascunho)
  setItens: (itens: AddItemDto[]) => void;
  setTabelaPreco: (id: number | null) => void;
  // AIDEV-NOTE: merge parcial dos filtros do catalogo
  setFiltrosCatalogo: (patch: Partial<FiltrosCatalogo>) => void;
  setStepAtual: (step: number) => void;
  setPedidoId: (id: string) => void;
  setDraftOrigem: (origem: 'novo' | 'edicao' | null) => void;
  setDraftVendedorId: (vendedorId: number | null) => void;
  resetPedido: () => void;
}

const initialState = {
  clienteSelecionado: null,
  itens: [],
  precosOriginais: [],
  tabelaPrecoId: null,
  filtrosCatalogo: {
    busca: '',
    secao: '',
    grupo: '',
    subgrupo: '',
    filtrosAbertos: false,
    caminhoArvore: []
  } as FiltrosCatalogo,
  stepAtual: 0,
  pedidoId: null,
  draftOrigem: null,
  draftVendedorId: null
};

// AIDEV-NOTE: Finding 1 + 3 (final review) — helper puro de decisao usado pela NovoPedidoPage
// para saber se o draft persistido pode ser oferecido para recuperacao. Extraido como funcao pura
// (sem acesso direto ao store) para ser testavel com vitest sem precisar montar React/Zustand.
// Regras: draft de edicao nunca e oferecido em /pedidos/novo (Finding 1); draft de outro vendedor
// nunca e oferecido em dispositivo compartilhado (Finding 3); draftVendedorId null e tratado como
// match — nao pune drafts criados antes deste fix (persistidos sem o campo).
export function podeRecuperarDraft(
  draft: {
    itens: unknown[];
    pedidoId: string | null;
    draftOrigem: 'novo' | 'edicao' | null;
    draftVendedorId: number | null;
  },
  vendedorAtual: number | null | undefined
): boolean {
  if (draft.itens.length === 0 || !draft.pedidoId) return false;
  if (draft.draftOrigem !== 'novo') return false;
  const vendedorAtualNormalizado = vendedorAtual ?? null;
  if (
    draft.draftVendedorId !== null &&
    vendedorAtualNormalizado !== null &&
    draft.draftVendedorId !== vendedorAtualNormalizado
  ) {
    return false;
  }
  return true;
}

// AIDEV-NOTE: Store Zustand para o wizard de criacao de pedido.
// Itens sao AddItemDto (schema compartilhado com o server).
// AIDEV-NOTE: persist em localStorage — o carrinho sobrevive a reload/logout forcado/
// crash do tab (incidente 2026-08: oscilacao de rede + reload apagou pedido de R$38k).
// filtrosCatalogo e stepAtual transientes ficam de fora do partialize? stepAtual FICA:
// retomar no mesmo step e parte da recuperacao. filtrosCatalogo fica de fora (UI volatil).
export const usePedidoStore = create<PedidoStore>()(
  persist(
    (set) => ({
      ...initialState,

      setCliente: (c) => set({ clienteSelecionado: c }),

      addItem: (item) =>
        set((state) => {
          // AIDEV-NOTE: Agrupa itens com mesmo produto/cor/tamanho/preco em vez de duplicar
          const existingIdx = state.itens.findIndex(
            (i) =>
              i.produto_codigo === item.produto_codigo &&
              (i.codcor ?? 0) === (item.codcor ?? 0) &&
              (i.codtamanho ?? 0) === (item.codtamanho ?? 0) &&
              i.valor_unitario === item.valor_unitario
          );
          if (existingIdx >= 0) {
            const updated = [...state.itens];
            const existing = updated[existingIdx]!;
            updated[existingIdx] = {
              ...existing,
              quantidade: existing.quantidade + item.quantidade
            };
            // precosOriginais não muda — o item já existia com seu preço original
            return { itens: updated };
          }
          return {
            itens: [...state.itens, item],
            precosOriginais: [...state.precosOriginais, item.valor_unitario]
          };
        }),

      removeItem: (index) =>
        set((state) => ({
          itens: state.itens.filter((_, i) => i !== index),
          precosOriginais: state.precosOriginais.filter((_, i) => i !== index)
        })),

      updateItem: (index, updates) =>
        set((state) => ({
          // AIDEV-NOTE: precosOriginais NÃO é atualizado — guarda o preço no momento da adição
          itens: state.itens.map((item, i) => (i === index ? { ...item, ...updates } : item))
        })),

      // AIDEV-NOTE: Substitui todos os itens — usado ao carregar rascunho para edicao.
      // precosOriginais é inicializado com os valor_unitario atuais (não temos o original neste caso).
      setItens: (itens) => set({ itens, precosOriginais: itens.map((i) => i.valor_unitario) }),

      setTabelaPreco: (id) => set({ tabelaPrecoId: id }),

      setFiltrosCatalogo: (patch) =>
        set((state) => ({ filtrosCatalogo: { ...state.filtrosCatalogo, ...patch } })),

      setStepAtual: (step) => set({ stepAtual: step }),

      setPedidoId: (id) => set({ pedidoId: id }),

      setDraftOrigem: (origem) => set({ draftOrigem: origem }),

      setDraftVendedorId: (vendedorId) => set({ draftVendedorId: vendedorId }),

      resetPedido: () => set(initialState)
    }),
    {
      name: 'azoup-pedido-draft',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        clienteSelecionado: s.clienteSelecionado,
        itens: s.itens,
        precosOriginais: s.precosOriginais,
        tabelaPrecoId: s.tabelaPrecoId,
        stepAtual: s.stepAtual,
        pedidoId: s.pedidoId,
        draftOrigem: s.draftOrigem,
        draftVendedorId: s.draftVendedorId
      })
    }
  )
);
